export interface IConfig {
  weekStartDay: Days;
  year: number;
  month: number;
}

export interface IDayData {
  date: Date;
  day: number;
  dateKey: string;
  isInPrimaryMonth: boolean;
  isInLastWeekOfPrimaryMonth: boolean;
  index: { day: number; week: number };
}

export enum Days {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

const defaultConfig: Partial<IConfig> = {
  weekStartDay: Days.Sunday,
};

function getStartDayOffset(startDay: Days) {
  switch (startDay) {
    case Days.Monday:
      return 0;
    case Days.Tuesday:
      return -1;
    case Days.Wednesday:
      return -2;
    case Days.Thursday:
      return -3;
    case Days.Friday:
      return -4;
    case Days.Saturday:
      return -5;
    default:
      return 1;
  }
}

function createArray(length: number) {
  const a = [];

  for (let i = 0; i < length; i += 1) {
    a.push(1);
  }

  return a;
}

function padNumber(number: number, padTo = 2) {
  return `${number}`.padStart(padTo, '0');
}

function getISODateString(date: Date) {
  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}

function generate(
  year: number,
  month: number,
  firstWeekday: number,
) {
  const calendarWeeks: IDayData[][] = [];
  const totalWeeks = 6;
  const totalDaysOnWeek = 7;
  const lastWeek = totalWeeks - 1;

  let lastDay = firstWeekday * -1;
  let weeks: IDayData[] = [];

  createArray(totalWeeks).forEach((_, week) => {
    createArray(totalDaysOnWeek).forEach((__, day) => {
      lastDay += 1;

      const date = new Date(year, month, lastDay);

      const data: IDayData = {
        date,
        day: date.getDate(),
        isInPrimaryMonth: date.getMonth() === month,
        isInLastWeekOfPrimaryMonth: week === lastWeek,
        dateKey: getISODateString(date),
        index: {
          day,
          week,
        },
      };

      weeks.push(data);
    });

    calendarWeeks.push(weeks);

    weeks = [];
  });

  return calendarWeeks;
}

function generateCalendarWeeks(overrides: Partial<IConfig> = {}) {
  const config = { ...defaultConfig, ...overrides };

  const { weekStartDay, year, month } = config;

  if (month! < 0 || month! > 11) {
    throw new Error('Month should be between 0 and 11');
  }

  if (typeof year !== 'number' || typeof month !== 'number') {
    throw new Error('Arguments should be numbers');
  }

  const startDayOffset = getStartDayOffset(weekStartDay!);

  const firstWeekday = new Date(year, month, startDayOffset).getDay();

  return generate(
    year,
    month,
    firstWeekday,
  );
}

export default generateCalendarWeeks;